import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { motion } from 'framer-motion';
import FieldMappingTable from './components/FieldMappingTable';
import { formFeedsApi, MappingOptions } from '../../services/formFeedsApi';
import toast from 'react-hot-toast';
import Skeleton from '../../components/Skeleton';

const FieldMappingPage: React.FC = () => {
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();
  const [searchParams] = useSearchParams();
  const feedId = searchParams.get('feedId');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mappingOptions, setMappingOptions] = useState<MappingOptions | null>(null);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});

  useEffect(() => {
    if (formId) {
      fetchMappingOptions();
    }
  }, [formId]);

  useEffect(() => {
    if (feedId) {
      fetchExistingMappings();
    }
  }, [feedId]);

  const fetchMappingOptions = async () => {
    try {
      setLoading(true);
      const options = await formFeedsApi.getMappingOptions(Number(formId));
      setMappingOptions(options);
    } catch (error: any) {
      toast.error('Failed to load mapping options');
      navigate('/form-feeds');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingMappings = async () => {
    try {
      const feed = await formFeedsApi.getById(Number(feedId));
      setFieldMappings(feed.field_mappings || {});
    } catch (error: any) {
      toast.error('Failed to load existing mappings');
    }
  };

  const handleMappingChange = (projectFieldKey: string, formFieldName: string) => {
    setFieldMappings((prev) => ({
      ...prev,
      [projectFieldKey]: formFieldName,
    }));
  };

  const handleSave = async () => {
    if (!feedId) {
      toast.error('No feed ID provided');
      return;
    }

    try {
      setSaving(true);
      await formFeedsApi.update(Number(feedId), {
        field_mappings: fieldMappings,
      });
      toast.success('Field mappings saved successfully');
      navigate('/form-feeds');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all mappings?')) {
      setFieldMappings({});
      toast.success('All mappings cleared');
    }
  };

  const mappedFieldsCount = Object.values(fieldMappings).filter(value => value !== '').length;
  const totalFieldsCount = mappingOptions?.project_request_fields.length || 0;

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <div className="space-y-3">
            <Skeleton width={120} height={16} variant="rounded" />
            <Skeleton width={180} height={28} variant="rounded" />
            <Skeleton width={240} height={14} variant="rounded" />
          </div>
          <div className="flex gap-3">
            <Skeleton width={80} height={38} variant="rounded" />
            <Skeleton width={120} height={38} variant="rounded" />
          </div>
        </div>
        <div className="flex-1 theme-bg-primary rounded-lg shadow-sm border theme-border overflow-hidden flex flex-col">
          <div className="p-4 border-b theme-border">
            <Skeleton width={320} height={14} variant="rounded" />
          </div>
          <div className="flex-1 p-4 space-y-3">
            {/* Table header */}
            <div className="grid grid-cols-2 gap-4 pb-2 border-b theme-border">
              <Skeleton height={14} variant="rounded" />
              <Skeleton height={14} variant="rounded" />
            </div>
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="grid grid-cols-2 gap-4 items-center py-1">
                <Skeleton height={16} variant="rounded" />
                <Skeleton height={36} variant="rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!mappingOptions) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="theme-text-tertiary">Failed to load mapping options</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <div>
          <button
            onClick={() => navigate('/form-feeds')}
            className="flex items-center gap-2 theme-text-secondary hover:theme-text-primary transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Feeds
          </button>
          <h1 className="text-2xl font-bold theme-text-primary">Field Mapping</h1>
          <p className="text-sm theme-text-tertiary mt-1">
            Map form fields to project request fields ({mappedFieldsCount} of {totalFieldsCount} mapped)
          </p>
        </div>
        <div className="flex gap-3">
          {mappedFieldsCount > 0 && (
            <button
              onClick={handleClearAll}
              disabled={saving}
              className="px-4 py-2 theme-bg-tertiary hover:bg-red-100 dark:hover:bg-red-900/30 theme-text-primary hover:text-red-600 dark:hover:text-red-400 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear All
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !feedId}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Mappings'}
          </button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex-1 theme-bg-primary rounded-lg shadow-sm border theme-border overflow-hidden flex flex-col"
      >
        <div className="p-4 border-b theme-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-sm theme-text-secondary">
              Select form fields to map to each project request field. Unmapped fields will be ignored.
            </p>
            {mappedFieldsCount > 0 && (
              <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                {mappedFieldsCount} mapped
              </span>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scroll-smooth">
          <FieldMappingTable
            projectRequestFields={mappingOptions.project_request_fields}
            formFields={mappingOptions.form_fields}
            fieldMappings={fieldMappings}
            onMappingChange={handleMappingChange}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default FieldMappingPage;
